import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Form, ListGroup, Overlay } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Moment from 'react-moment';
import { useAppFilterSelectedValue } from '../../../../hooks/filters/use-app-filter-selected-value.hook';
import { IMAGES_REPO } from '../../../../static-resources';
import { DEFAULT_DATE_FORMAT } from '../../../../utils/formatting/date-formatter.helper';
import { ButtonDefault } from '../../../buttons/button-default.component';
import { ButtonPrimary } from '../../../buttons/button-primary.component';
import { getIdClassNameHelper } from '../../helpers/get-id-class-name.helper';
import { hasFilterSelectedValue } from '../../hooks/common.hook';
import { FilterComponentProps } from '../../interfaces/filter-component-props.interface';
import './period-selector.component.scss';
import '../datepicker/scss/datepicker.scss';

const defaultToday = new Date();
const WEEKS_QUICK_SELECTOR = [
  {
    id: '52weeks',
    value: -51,
    label: '52 Weeks',
  },
  {
    id: '26weeks',
    value: -25,
    label: '26 Weeks',
  },
];
const WEEKS_SELECTOR = [
  {
    id: '104weeks',
    value: -103,
    label: '104 Weeks',
  },
  {
    id: '52weeks',
    value: -51,
    label: '52 Weeks',
  },
  {
    id: '26weeks',
    value: -25,
    label: '26 Weeks',
  },
];
const MONTHS_SELECTOR = [
  {
    id: 'last6months',
    value: -6,
    label: 'Last 6 Months',
  },
  {
    id: 'lastmonth',
    value: -1,
    label: 'Last Month',
  },
  {
    id: 'currentmonth',
    value: 0,
    label: 'Current Month',
  },
  {
    id: 'ytd',
    value: 0,
    label: 'Year To Date',
  },
];
interface DateRange {
  startDate: Date;
  endDate: Date;
}

const getYTDRange = (lastUpdated: any): DateRange => {
  const startDate = moment(lastUpdated || defaultToday)
    .startOf('year')
    .toDate();
  const endDate = lastUpdated || defaultToday;
  return {
    startDate,
    endDate,
  };
};

const getLastNMonths = (months: number, lastUpdated: any): DateRange => {
  const startDate = moment(lastUpdated || defaultToday)
    .add(months, 'month')
    .date(1)
    .toDate();
  const endDate = lastUpdated || defaultToday;
  return {
    startDate,
    endDate,
  };
};
const getLastMonth = (months: number): DateRange => {
  const startDate = moment(defaultToday).add(months, 'month').date(1).toDate();
  const endDate = moment(defaultToday)
    .add(months, 'month')
    .endOf('month')
    .toDate();
  return {
    startDate,
    endDate,
  };
};
interface PeriodSelectorComponentProps extends FilterComponentProps {
  filter: any;
  onApplyDateChange: (startDate: Date, endDate: Date, dateView: string) => void;
  lastAvailableDate: Date;
}
/**
 * Default period selector
 *
 */
export const PeriodSelectorComponent = ({
  lastAvailableDate,
  filter,
  onApplyDateChange,
}: PeriodSelectorComponentProps) => {
  const { selectedValue, setSelectedValue } = useAppFilterSelectedValue(
    filter.id,
  );
  const [sd, ed] = useMemo(() => {
    if (!selectedValue || selectedValue?.values.length === 0)
      return [defaultToday, defaultToday];
    const sd = moment(selectedValue?.values[0].value).toDate();
    const ed = moment(selectedValue?.values[1].value).toDate();
    return [sd, ed];
  }, [selectedValue]);
  useEffect(() => {
    if (selectedValue) {
      setShow(false);
    }
  }, [selectedValue]);

  const [show, setShow] = useState(false);
  const target = useRef(null);

  const [startDate, setStartDate] = useState<Date>(defaultToday);
  const [endDate, setEndDate] = useState<Date>(defaultToday);
  const [dateView, setDateView] = useState<string>('Week');

  const [startDateInput, setStartDateInput] = useState<string>(
    moment(startDate).format(DEFAULT_DATE_FORMAT),
  );
  const [endDateInput, setEndDateInput] = useState<string>(
    moment(endDate).format(DEFAULT_DATE_FORMAT),
  );

  useEffect(() => {
    setEndDate(lastAvailableDate); // Set endDate to lastUpdated
    setEndDateInput(
      moment(lastAvailableDate).utc().format(DEFAULT_DATE_FORMAT),
    );
  }, [lastAvailableDate]);

  useEffect(() => {
    const val = hasFilterSelectedValue(selectedValue);
    if (val) {
      const sd = moment(val?.values[0].value);
      const ed = moment(val?.values[1].value);

      setStartDate(sd.toDate());
      setEndDate(ed.toDate());
      setStartDateInput(sd.utc().format(DEFAULT_DATE_FORMAT));
      setEndDateInput(ed.utc().format(DEFAULT_DATE_FORMAT));
    }
  }, [selectedValue]);

  const setWeeksRangeTmp = useCallback(
    (weeks: number) => {
      const startDate = moment(lastAvailableDate);
      const newSd = startDate.add(weeks, 'week').toDate();
      const newEd = lastAvailableDate;
      setStartDate(newSd);
      setEndDate(newEd);
      setStartDateInput(moment(newSd).utc().format(DEFAULT_DATE_FORMAT));
      setEndDateInput(moment(newEd).utc().format(DEFAULT_DATE_FORMAT));
      setDateView('Week');
    },
    [
      lastAvailableDate,
      setStartDate,
      setEndDate,
      setDateView,
      setStartDateInput,
      setEndDateInput,
    ],
  );

  const setMonthRangeTmp = useCallback(
    (months: number, id: string) => {
      let range: DateRange | undefined;
      switch (id) {
        case 'lastmonth':
        case 'currentmonth':
          range = getLastMonth(months);
          setDateView('Week');
          break;
        case 'ytd':
          range = getYTDRange(endDate);
          setDateView('Week');
          break;
        default:
          range = getLastNMonths(months, endDate);
          setDateView('Month');
      }
      if (range) {
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setStartDateInput(
          moment(range.startDate).utc().format(DEFAULT_DATE_FORMAT),
        );
        setEndDateInput(
          moment(range.endDate).utc().format(DEFAULT_DATE_FORMAT),
        );
      }
    },
    [endDate],
  );

  const onChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setStartDateInput(moment(start).utc().format(DEFAULT_DATE_FORMAT));
    setEndDateInput(
      moment(end || defaultToday)
        .utc()
        .format(DEFAULT_DATE_FORMAT),
    );
    setDateView('Week');
  };

  const applyChanges = useCallback(() => {
    if (setSelectedValue && selectedValue && endDate) {
      setSelectedValue({
        ...selectedValue,
        values: [
          {
            value: startDate.toISOString(),
            label: moment(startDate).format(DEFAULT_DATE_FORMAT),
          },
          {
            value: endDate.toISOString(),
            label: moment(endDate).format(DEFAULT_DATE_FORMAT),
          },
        ],
      });

      // Pass the dateView to the parent
      onApplyDateChange(startDate, endDate, dateView);
    }
  }, [
    setSelectedValue,
    selectedValue,
    endDate,
    startDate,
    onApplyDateChange,
    dateView,
  ]);

  const setWeeksRange = useCallback(
    (weeks: number) => {
      const startDate = moment(lastAvailableDate);
      const newSd = startDate.add(weeks, 'week').toDate();
      const newEd = lastAvailableDate;
      if (setSelectedValue && selectedValue) {
        setSelectedValue({
          ...selectedValue,
          values: [
            {
              value: newSd.toISOString(),
              label: moment(newSd).format(DEFAULT_DATE_FORMAT),
            },
            {
              value: newEd.toISOString(),
              label: moment(newEd).format(DEFAULT_DATE_FORMAT),
            },
          ],
        });
      }
      setDateView('Week');

      // Pass the dateView to the parent
      onApplyDateChange(newSd, newEd, dateView);
    },
    [
      lastAvailableDate,
      setSelectedValue,
      selectedValue,
      onApplyDateChange,
      dateView,
    ],
  );

  const handleStartDateInputChange = (e: any) => {
    const inputDate = e.target.value;
    setStartDateInput(inputDate);

    const parsedDate = moment(inputDate, DEFAULT_DATE_FORMAT, true);
    if (
      parsedDate.isValid() &&
      (!endDate || parsedDate.isSameOrBefore(endDate))
    ) {
      setStartDate(parsedDate.toDate());
    }
  };

  const handleEndDateInputChange = (e: any) => {
    const inputDate = e.target.value;
    setEndDateInput(inputDate);

    const parsedDate = moment(inputDate, DEFAULT_DATE_FORMAT, true);
    if (parsedDate.isValid() && parsedDate.isSameOrAfter(startDate)) {
      setEndDate(parsedDate.toDate());
    }
  };

  return (
    <div className={`${getIdClassNameHelper(filter.id)} period-selector`}>
      <ButtonGroup>
        <Button
          variant="sm"
          className="period-dates-btn btn-default"
          ref={target}
          onClick={() => setShow(!show)}
        >
          <div className="period-selector-label">{filter.name}</div>
          <img src={IMAGES_REPO.CalendarIcon} alt="" />
          &nbsp;
          <span>
            <Moment format={DEFAULT_DATE_FORMAT} utc={true}>
              {sd}
            </Moment>
          </span>
          &nbsp;|&nbsp;
          <span>
            <Moment format={DEFAULT_DATE_FORMAT} utc={true}>
              {ed}
            </Moment>
          </span>
        </Button>
        {WEEKS_QUICK_SELECTOR.map((i, index) => (
          <ButtonDefault
            key={index}
            variant="sm"
            className={`${i.id} btn-default`}
            onClick={() => setWeeksRange(i.value)}
          >
            {index === 0 && (
              <div className="period-selector-buttons-label">
                {filter.name} Length
              </div>
            )}
            {i.label}
          </ButtonDefault>
        ))}
      </ButtonGroup>
      <Overlay target={target.current} show={show} placement="bottom-start">
        {({ ...props }) => (
          <div {...props} className="d-flex period-selector-popover">
            <div className="d-flex period-selector-buttons-container">
              <ListGroup>
                {WEEKS_SELECTOR.map((i, index) => (
                  <ListGroup.Item
                    key={index}
                    className={`${i.id} period-selector-button`}
                    action
                    onClick={() => setWeeksRangeTmp(i.value)}
                  >
                    {i.label}
                  </ListGroup.Item>
                ))}
                {MONTHS_SELECTOR.map((i, index) => (
                  <ListGroup.Item
                    key={index}
                    className={`${i.id} period-selector-button`}
                    action
                    onClick={() => setMonthRangeTmp(i.value, i.id)}
                  >
                    {i.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
            <div className="period-selector-body">
              <div className="period-selector-datepicker-container">
                <DatePicker
                  selected={startDate}
                  onChange={onChange}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange={true}
                  inline={true}
                  monthsShown={2}
                />
              </div>
              <div className="d-flex period-selector-footer">
                <Form.Control
                  type="text"
                  className="start-date-input"
                  value={startDateInput}
                  onChange={handleStartDateInputChange}
                  placeholder="Start Date"
                />
                &nbsp;-&nbsp;
                <Form.Control
                  type="text"
                  className="end-date-input"
                  value={endDateInput}
                  onChange={handleEndDateInputChange}
                  placeholder="End Date"
                />
                <div className="ms-auto">
                  <ButtonDefault
                    className="btn-white"
                    onClick={() => setShow(false)}
                  >
                    Cancel
                  </ButtonDefault>
                </div>
                <div className="ms-3">
                  <ButtonPrimary onClick={() => applyChanges()}>
                    Apply
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};
